import React, {ButtonHTMLAttributes, CSSProperties, MouseEvent, ReactNode, forwardRef} from 'react'

import ToolTip from '../feedback/ToolTip.js'
import Spinner from '../feedback/Spinner.js'
import {markFieldRowControl, useFieldRowOffset} from '../forms/FormRowContext.js'
import {focusableClass, ensureFocusStyles} from '../styles.js'
import {ButtonSize, ButtonVariant, buttonSizes, getButtonColors, getSpinnerColor} from './styles.js'
import {useClickGuard} from './useClickGuard.js'

ensureFocusStyles()

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'style' | 'color' | 'type'> {
    /** Style of the outer element: its place and size in the layout (margins, width, flex, position) */
    style?: CSSProperties
    /** Style of the <button> itself, for its look only (padding, corners, border), see SplitButton. Set the height with size */
    buttonStyle?: CSSProperties
    type?: 'button' | 'submit' | 'reset'
    variant?: ButtonVariant
    /** Height of the button, md (36) by default, lg (48) with labelPosition 'bottom' */
    size?: ButtonSize
    /** Background of a solid button instead of the default grey */
    color?: string
    /** Outline and text buttons get a border */
    selected?: boolean
    icon?: string
    /** Icon after the label */
    afterIcon?: string
    label?: ReactNode
    /** 'bottom': the label below the icon */
    labelPosition?: 'right' | 'bottom'
    loading?: boolean
    tooltip?: string
    tooltipPlacement?: 'right'
    children?: ReactNode
}

/**
 * A button with an icon and/or label (or other children). The outer element takes the style prop and places the
 * button in the layout, the <button> fills it and gets the other props (aria-*, onMouseDown, form...) and the ref.
 * Clicks within 200ms of the previous one are ignored, also for submit buttons. Loading shows a spinner and disables
 * the button. In a FormRow the button centres itself on the fields.
 */
const Button = forwardRef<HTMLButtonElement, ButtonProps>(({
    style = {},
    buttonStyle,
    type = 'button',
    variant = 'solid',
    labelPosition = 'right',
    size = labelPosition === 'bottom' ? 'lg' : 'md',
    color,
    selected,
    icon,
    afterIcon,
    label,
    loading,
    disabled,
    tooltip,
    tooltipPlacement,
    onClick,
    onMouseDown,
    children,
    className,
    ...rest
}, ref) => {
    const height = typeof style.height === 'number' ? style.height : buttonSizes.height[size]
    // Centres the button on the fields when it sits in a FormRow
    const rowOffset = useFieldRowOffset(height)
    const guardClick = useClickGuard()

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
        if (guardClick(event)) {
            onClick?.(event)
        }
    }

    const hasLabel = label !== undefined && label !== null && label !== ''
    const hasText = hasLabel || (children !== undefined && children !== null && children !== false)
    const inactive = disabled || loading
    // The nav variant has its own icon size, padding and font
    const sizes = variant === 'nav' ? {...buttonSizes, ...buttonSizes.nav} : buttonSizes

    const rootStyle: CSSProperties = {
        position: 'relative',
        height,
        // Its content's width, unless the caller lets it flex
        width: style.flex === undefined ? 'fit-content' : undefined,
        ...rowOffset,
        ...style
    }

    const defaultStyle: CSSProperties = {
        display: 'flex',
        flexDirection: labelPosition === 'bottom' ? 'column' : 'row',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '100%',
        paddingLeft: sizes.paddingX,
        paddingRight: sizes.paddingX,
        borderRadius: sizes.radius,
        fontSize: typeof children === 'string' ? sizes.fontSize : sizes.iconSize,
        fontWeight: sizes.fontWeight,
        letterSpacing: sizes.letterSpacing,
        cursor: disabled ? 'default' : 'pointer',
        ...getButtonColors({variant, color, disabled, selected}),
        ...buttonStyle
    }

    // The icon's gap to the label at its right, none when the label is below it
    const iconGap = hasText && labelPosition !== 'bottom' ? sizes.iconGap : 0

    const button = (
        <button
            // An icon-only button is named by its tooltip, unless the caller names it
            aria-label={!hasText && tooltip ? tooltip : undefined}
            aria-busy={loading || undefined}
            {...rest}
            ref={ref}
            className={className ? `${focusableClass} ${className}` : focusableClass}
            style={defaultStyle}
            type={type}
            disabled={inactive}
            onClick={inactive ? undefined : handleClick}
            onMouseDown={loading ? undefined : onMouseDown}
        >
            {loading ?
                <Spinner color={getSpinnerColor({variant, color})} /> :
                <>
                    {icon && <i aria-hidden className={icon} style={{marginRight: iconGap, fontSize: sizes.iconSize}} />}
                    {hasLabel ? <div style={{fontSize: sizes.fontSize}}>{label}</div> : children}
                    {afterIcon && <i aria-hidden className={afterIcon} style={{marginLeft: hasText ? sizes.iconGap : 0, fontSize: sizes.iconSize}} />}
                </>
            }
        </button>
    )

    if (tooltip) {
        return (
            <ToolTip text={tooltip} placement={tooltipPlacement} style={rootStyle}>
                {button}
            </ToolTip>
        )
    }

    return <div style={rootStyle}>{button}</div>
})

Button.displayName = 'Button'

export default markFieldRowControl(Button)
